/**
 * The single authoritative permission matrix.
 *
 * Consumed by TWO enforcement points:
 *   1. `rbac()` middleware in the Worker — the actual security boundary.
 *   2. The React UI — to hide controls a user cannot use.
 *
 * One definition, two consumers, zero drift. The UI hiding a button is a
 * courtesy; the middleware is what makes it safe. Never rely on the former.
 *
 * Access model (docs/01-architecture.md §6):
 *   • System permissions come from `users.system_role`.
 *   • Board permissions come from `board_members.role` for THAT board.
 *   • Admins and Super Admins additionally hold board permissions on every
 *     board, because they administer the workspace.
 */

import { BOARD_ROLES, SYSTEM_ROLES, type BoardRole, type SystemRole } from './enums';

// ------------------------------------------------------ system-level abilities

export const SYSTEM_PERMISSIONS = [
  'admin.create',
  'admin.revoke',
  'user.create',
  'user.update',
  'user.deactivate',
  'user.list',
  'workspace.update',
  'system.settings',
  'system.storage',
  'system.logs',
  'audit.read',
  'board.create',
  'board.administer_any',
] as const;
export type SystemPermission = (typeof SYSTEM_PERMISSIONS)[number];

// ------------------------------------------------------- board-level abilities

export const BOARD_PERMISSIONS = [
  'board.view',
  'board.update',
  'board.archive',
  'board.delete',
  'board.duplicate',
  'board.export',
  'board.member.manage',
  'list.create',
  'list.update',
  'list.move',
  'list.archive',
  'card.create',
  'card.update',
  'card.move',
  'card.archive',
  'card.delete',
  'label.manage',
  'comment.create',
  'comment.moderate',
  'checklist.manage',
  'attachment.upload',
  'attachment.delete',
] as const;
export type BoardPermission = (typeof BOARD_PERMISSIONS)[number];

export type Permission = SystemPermission | BoardPermission;

// --------------------------------------------------------------- the matrices

const MEMBER_SYSTEM: readonly SystemPermission[] = [];

const ADMIN_SYSTEM: readonly SystemPermission[] = [
  'user.create',
  'user.update',
  'user.deactivate',
  'user.list',
  'workspace.update',
  'audit.read',
  'board.create',
  'board.administer_any',
];

const SUPER_ADMIN_SYSTEM: readonly SystemPermission[] = [
  ...ADMIN_SYSTEM,
  'admin.create',
  'admin.revoke',
  'system.settings',
  'system.storage',
  'system.logs',
];

export const SYSTEM_ROLE_PERMISSIONS: Record<SystemRole, readonly SystemPermission[]> = {
  member: MEMBER_SYSTEM,
  admin: ADMIN_SYSTEM,
  super_admin: SUPER_ADMIN_SYSTEM,
};

const VIEWER_BOARD: readonly BoardPermission[] = ['board.view'];

const EDITOR_BOARD: readonly BoardPermission[] = [
  'board.view',
  'board.export',
  'board.update',
  'list.create',
  'list.update',
  'list.move',
  'list.archive',
  'card.create',
  'card.update',
  'card.move',
  'card.archive',
  'label.manage',
  'comment.create',
  'checklist.manage',
  'attachment.upload',
  'attachment.delete',
];

const OWNER_BOARD: readonly BoardPermission[] = [
  ...EDITOR_BOARD,
  'board.archive',
  'board.delete',
  'board.duplicate',
  'board.member.manage',
  'card.delete',
  'comment.moderate',
];

export const BOARD_ROLE_PERMISSIONS: Record<BoardRole, readonly BoardPermission[]> = {
  viewer: VIEWER_BOARD,
  editor: EDITOR_BOARD,
  owner: OWNER_BOARD,
};

// ------------------------------------------------------------------- checkers

const SYSTEM_PERMISSION_SET = new Set<string>(SYSTEM_PERMISSIONS);

export function isSystemPermission(permission: Permission): permission is SystemPermission {
  return SYSTEM_PERMISSION_SET.has(permission);
}

/** Does this system role hold this system-level permission? */
export function hasSystemPermission(role: SystemRole, permission: SystemPermission): boolean {
  return SYSTEM_ROLE_PERMISSIONS[role].includes(permission);
}

/** Does this board role hold this board-level permission? */
export function hasBoardPermission(role: BoardRole, permission: BoardPermission): boolean {
  return BOARD_ROLE_PERMISSIONS[role].includes(permission);
}

/** The caller's resolved authorisation context for a single request. */
export interface AccessContext {
  systemRole: SystemRole;
  /** The caller's role on the board in question, if any. */
  boardRole?: BoardRole | undefined;
}

/**
 * The one function both the Worker and the UI call.
 *
 * Board permissions resolve in this order:
 *   1. `board.administer_any` (Admin / Super Admin) ⇒ treated as board owner.
 *   2. Otherwise the caller's explicit `board_members.role`.
 *   3. Otherwise denied.
 */
export function can(context: AccessContext, permission: Permission): boolean {
  if (isSystemPermission(permission)) {
    return hasSystemPermission(context.systemRole, permission);
  }

  if (hasSystemPermission(context.systemRole, 'board.administer_any')) {
    return hasBoardPermission('owner', permission);
  }

  if (!context.boardRole) return false;
  return hasBoardPermission(context.boardRole, permission);
}

/** Convenience for guarding a UI region on several permissions at once. */
export function canAll(context: AccessContext, permissions: readonly Permission[]): boolean {
  return permissions.every((permission) => can(context, permission));
}

export function canAny(context: AccessContext, permissions: readonly Permission[]): boolean {
  return permissions.some((permission) => can(context, permission));
}

/**
 * Only a Super Admin may change who is an Admin, and nobody may act on a
 * Super Admin other than a Super Admin. This blocks the privilege-escalation
 * loop where two Admins promote each other.
 */
export function canManageUserWithRole(actor: SystemRole, target: SystemRole): boolean {
  if (actor === 'super_admin') return true;
  if (actor === 'admin') return target === 'member';
  return false;
}

export const ALL_SYSTEM_ROLES = SYSTEM_ROLES;
export const ALL_BOARD_ROLES = BOARD_ROLES;
